<template>
  <div class="driver-content manager">
    <LoaderOverlay v-if="loading" />

    <div v-else-if="currentTrip">
      <!-- Filtres -->
      <section class="filters form-group">
        <div class="btn-group">
          <div>
            <button
              @click="selectValidation('all')"
              class="btn btn-chip"
              :class="{ 'is-active': filters.validated === 'all' }"
            >
              Tous
            </button>
            <button
              @click="selectValidation('yes')"
              class="btn btn-chip"
              :class="{ 'is-active': filters.validated === 'yes' }"
            >
              Validés
            </button>
            <button
              @click="selectValidation('no')"
              class="btn btn-chip"
              :class="{ 'is-active': filters.validated === 'no' }"
            >
              Non validés
            </button>
          </div>
        <!-- Recherche -->
          <div class="filters-group">
          <input
            v-model="filters.searchText"
            type="text"
            placeholder="Rechercher par téléphone ou code..."
            class="form-control"
          />
        </div>
        </div>
      </section>


      <p class="results-count">
        {{ filteredTickets.length }} {{ ticketsLabel }}
      </p>

      <!-- Liste des tickets -->
      <section v-if="filteredTickets.length > 0" class="listing" role="table" aria-colcount="3">
        <div class="listing__body" role="rowgroup">
          <div
            v-for="ticket in filteredTickets"
            :key="ticket.uuid"
            class="listing__row"
            :class="{ 'is-validated': ticket.isValidated, 'is-expanded': expandedTicket === ticket.uuid }"
            role="row"
            @click="toggleTicketDetails(ticket.uuid)"
          >
            <div class="listing__cell listing__cell--status" role="cell">
              <span class="badge" :class="getStatusClass(ticket.status)">
                {{ getStatusLabel(ticket.status) }}
              </span>
            </div>

            <div class="listing__cell listing__cell--phone" role="cell">
              <span class="text-phone">{{ getDisplayPhone(ticket) }}</span>
            </div>

            <!-- <div class="listing__cell listing__cell--code" role="cell">
              <div v-if="expandedTicket === ticket.uuid && !ticket.isValidated"
                class="validation-code-mini">
                <strong>{{ ticket.validationCode }}</strong>
              </div>
              <div v-else-if="ticket.isValidated" class="validation-code-mini-hidden">
              </div>
              <div v-else class="validation-code-mini-hidden">
                <span>●●●</span>
              </div>
            </div>-->
            <div class="listing__cell listing__cell--action" role="cell" @click.stop>
              <button
                v-if="!ticket.isValidated"
                @click="validateTicket(ticket)"
                class="btn btn-chip"
                :disabled="validating === ticket.uuid"
              >
                {{ validating === ticket.uuid ? 'Validation...' : 'Valider' }}
              </button>
              <span v-else class="badge badge-validated">✓ Validé</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Message si aucun ticket -->
      <EmptyState
        v-else
        :is-empty="true"
        emptyMessage="Aucun ticket pour ce filtre."
      />
    </div>

    <!-- Message si pas de trajet en cours -->
    <EmptyState
      v-else
      :is-empty="true"
      emptyMessage="Aucun trajet en cours."
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { config } from '@/config/env'
import LoaderOverlay from '@/components/LoaderOverlay.vue'
import EmptyState from '@/components/EmptyState.vue'
import { useToast } from 'vue-toast-notification'
import { useModalStore } from '@/stores/modalStore'

const toast = useToast()
const loading = ref(false)
const validating = ref(null)
const currentTrip = ref(null)
const tickets = ref([])
const expandedTicket = ref(null)
const modalStore = useModalStore()

const filters = ref({
  validated: 'no',
  searchText: ''
})

onMounted(async () => {
  await checkCurrentTrip()
})

async function checkCurrentTrip() {
  loading.value = true
  try {
    const response = await fetch(config.apiUrl + '/trip/driver/current', {
      credentials: 'include'
    })

    if (response.ok) {
      const data = await response.json()
      if (data.uuid) {
        currentTrip.value = data
        await loadTripTickets(data.uuid)
      }
    }
  } catch (err) {
    console.error('Erreur lors de la récupération du trajet en cours:', err)
    toast.error('Erreur lors du chargement du trajet')
  } finally {
    loading.value = false
  }
}

async function loadTripTickets(tripUuid) {
  try {
    const response = await fetch(config.apiUrl + `/trip/driver/${tripUuid}`, {
      credentials: 'include'
    })

    if (response.ok) {
      const data = await response.json()
      if (data.message) {
        tickets.value = []
      } else if (Array.isArray(data)) {
        tickets.value = data
      } else {
        tickets.value = data.tickets || []
      }
    }
  } catch (err) {
    console.error('Erreur lors de la récupération des tickets:', err)
    toast.error('Erreur lors du chargement des tickets')
  }
}

function selectValidation(value) {
  filters.value.validated = value
}

function toggleTicketDetails(ticketUuid) {
  expandedTicket.value = expandedTicket.value === ticketUuid ? null : ticketUuid
}

function getDisplayPhone(ticket) {
  // Priorité à ticket.userPhone, sinon ticket.payerPhone
  let phone = ticket.payerPhone ?? ticket.userPhone;

  if (!phone) {
    return '—';
  }

  // Si le numéro commence déjà par '+', on retourne tel quel
  if (phone.startsWith('+')) {
    return phone;
  } else {
    return '+' + phone;
  }
}

function formatDateTime(dateString) {
  if (!dateString) return '—'

  const date = new Date(dateString)
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const filteredTickets = computed(() => {
  let result = [...tickets.value]

  // Filtre par statut de validation
  if (filters.value.validated === 'yes') {
    result = result.filter(ticket => ticket.isValidated === true)
  } else if (filters.value.validated === 'no') {
    result = result.filter(ticket => ticket.isValidated === false)
  }

  // Filtre par téléphone ou code de validation
  if (filters.value.searchText.trim()) {
    const searchLower = filters.value.searchText.toLowerCase()
    result = result.filter(ticket => {
      const phone = (ticket.payerPhone ?? ticket.userPhone ?? '').toLowerCase()
      const code = (ticket.validationCode ?? '').toLowerCase()
      return phone.includes(searchLower) || code.includes(searchLower)
    })
  }

  return result
})

const ticketsLabel = computed(() =>
  filteredTickets.value.length > 1 ? 'tickets' : 'ticket'
)

function getStatusClass(status) {
  const map = {
    pending_payment: 'badge-pending-payment',
    paid: 'badge-paid',
    used: 'badge-used',
    validated: 'badge-validated',
    cancelled: 'badge-cancelled',
    refunded: 'badge-refunded'
  }
  return map[(status || '').toLowerCase()] || 'badge-neutral'
}

function getStatusLabel(status) {
  const labels = {
    pending_payment: 'En attente',
    paid: 'Payé',
    used: 'Utilisé',
    validated: 'Validé',
    cancelled: 'Annulé',
    refunded: 'Remboursé'
  }
  return labels[(status || '').toLowerCase()] || status
}

async function validateTicket(ticket) {
  const { result } = modalStore.open({
    kind: 'confirm',
    title: 'Validation du ticket',
    message: `Confirmer la validation du ticket de ${getDisplayPhone(ticket)} avec le code ${ticket.validationCode} ?`,
    confirmText: 'Valider',
    cancelText: 'Annuler'
  })

  const res = await result
  if (res.action !== 'confirm') {
    return
  }

  validating.value = ticket.uuid
  try {
    const response = await fetch(config.apiUrl + `/ticket/validate/${ticket.uuid}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        validationCode: ticket.validationCode
      })
    })

    if (response.ok) {
      ticket.isValidated = true
      toast.success('Ticket validé avec succès')
    } else {
      const data = await response.json()
      toast.error(data.message || 'Erreur lors de la validation')
    }
  } catch (err) {
    console.error('Erreur lors de la validation du ticket:', err)
    toast.error('Erreur lors de la validation du ticket')
  } finally {
    validating.value = null
  }
}
</script>

<style scoped>
.filters .btn-group > div:first-child {
  flex-flow: unset;
  flex: unset;
}
.filters .btn-group > div:last-child {
}

.filters-group {
  flex: 1;
}

.filters .btn-group {
  display: flex;
  gap: 1rem;
  flex-flow: row wrap;
}

.filters .btn-group input {
  min-width: 200px;
}

.listing__cell--action {
  text-align: right;
  /* flex: 1; */
}
.validation-code-mini-hidden {
  font-size: 1.2rem;
  color: var(--color-text-secondary);
  letter-spacing: 0.2em;
}

.text-phone {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  /* direction: rtl; */
}

</style>
